<template>
    <div class="h-full overflow-hidden bg-white flex transition-all duration-200 ease-in-out flex-col min-w-[370px]">
        <div class="w-full border-t-[6px] border-t-blue-500 flex border-b border-b-gray-300 h-16 items-center justify-center">
            <div class="font-bold text-lg mr-2 select-none">
                Соединения и файлы
            </div>
        </div>
        <div class="flex items-center justify-between">
            <div 
                class="border-b-2 cursor-pointer select-none text-center text-base w-1/2 py-2"
                :class="{
                    'text-blue-600 border-b-blue-500': currentState === 'Соединения',
                    'text-gray-600 border-transparent': currentState !== 'Соединения'
                }"
                v-on:click="setState('Соединения')"
            >Соединения</div>
            <div 
                class="border-b-2 cursor-pointer select-none text-center text-base w-1/2 py-2"
                :class="{
                    'text-blue-600 border-b-blue-500': currentState === 'Файлы',
                    'text-gray-600 border-transparent': currentState !== 'Файлы'
                }"
                v-on:click="setState('Файлы')"
            >Файлы</div>
        </div>
        <div v-if="currentState === 'Файлы'" class="w-full h-full flex flex-col items-center overflow-y-auto">
            <div v-for="file in files" v-bind:key="file"
                 class="flex items-center h-14 hover:bg-gray-100 border-t border-t-gray-200 w-full"
            >
                <div class="m-4">
                    <FileSvg/>
                </div>
                <div class="ml-2">
                    <div class="block whitespace-nowrap text-ellipsis overflow-hidden w-52 font-bold text-xs select-none">
                        {{ file.file_name }}
                    </div>
                    <div class="text-[10px] select-none">
                        Type: Google Sheets
                    </div>
                </div>
                <div class="size-10 ml-auto mr-10 flex justify-center items-center rounded-full hover:bg-gray-200 cursor-pointer">
                    <EditFileSvg v-on:click="openSpreadsheet(file.spreadsheet_id, file.tab_id)" />
                </div>
            </div>
        </div>
        <div class="w-full h-full flex flex-col items-center overflow-y-auto"
             v-if="currentState === 'Соединения'"
        >
            <router-link
                :to="{
                    name: 'CreateConnection',
                    params: {
                        'workflow_id': props.workflow_id
                    }
                }"
                v-if="connections.length <= 0"
                class="py-5 gap-2 cursor-pointer select-none hover:bg-gray-200 w-full flex items-center justify-center duration-150 transition-all"
            >
                <div>
                    <PlusCircleSvg class="size-6 stroke-[2px] stroke-blue-500"/>
                </div>
                <div class="font-bold text-sm text-blue-500">
                    Создайте новое соединение
                </div>
            </router-link>
            <draggable
                v-else
                class="border-t-gray-200 border-t w-full h-full"
                v-model="connections"
                @start="drag=true"
                @end="drag=false"
                item-key="id"
                handle=".handle"
                @change="updatePosition"
            >
                <template #item="{element}">
                    <div
                        class="flex items-center h-14 hover:bg-gray-100 border-b border-b-gray-200 w-full"
                        @mouseover="emits('hover-on', element.id)"
                        @mouseleave="emits('hover-off', element.id)"
                    >
                        <div class="group">
                            <div class="flex items-center justify-center size-10 select-none group-hover:hidden">
                                <div class="font-bold">
                                    {{ element.order_id }}
                                </div>
                            </div>
                            <div class="items-center justify-center size-10 cursor-pointer hidden group-hover:flex handle">
                                <svg viewBox="0 0 24 24" class="" data-role="drag-icon" style="width: 24px; height: 24px;" aria-labelledby="icon_labelledby_21998"><title id="icon_labelledby_21998">Drag to change running order</title><path d="M9,3H11V5H9V3M13,3H15V5H13V3M9,7H11V9H9V7M13,7H15V9H13V7M9,11H11V13H9V11M13,11H15V13H13V11M9,15H11V17H9V15M13,15H15V17H13V15M9,19H11V21H9V19M13,19H15V21H13V19Z" style="fill: rgb(97, 97, 97);"></path></svg>
                            </div>
                        </div>
                        <div class="justify-center flex flex-col">
                            <div class="font-bold text-xs select-none">
                                {{ element.name }}
                            </div>
                            <div class="flex justify-start items-center">
                                <svg class="size-3 fill-gray-600 mr-2" focusable="false" viewBox="0 0 24 24" aria-hidden="true" title="Last run"><path d="M21 10.12h-6.78l2.74-2.82c-2.73-2.7-7.15-2.8-9.88-.1-2.73 2.71-2.73 7.08 0 9.79s7.15 2.71 9.88 0C18.32 15.65 19 14.08 19 12.1h2c0 1.98-.88 4.55-2.64 6.29-3.51 3.48-9.21 3.48-12.72 0-3.5-3.47-3.53-9.11-.02-12.58s9.14-3.47 12.65 0L21 3v7.12zM12.5 8v4.25l3.5 2.08-.72 1.21L11 13V8h1.5z"></path></svg>
                                <div
                                    v-if="element.last_run_datetime !== null && element.last_run_datetime !== undefined"
                                    class="text-[10px] font-bold text-gray-600 select-none block whitespace-nowrap text-ellipsis overflow-hidden"
                                >
                                    {{ getFormattedDatetime(element.last_run_datetime) }}
                                </div>
                                <div
                                    v-else
                                    class="text-[10px] font-bold text-gray-600 select-none block whitespace-nowrap text-ellipsis overflow-hidden"
                                >
                                    Не запускалось
                                </div>
                            </div>
                        </div>
                        <div class="ml-auto mr-5 flex">
                            <div class="group/run mr-3 flex justify-center items-center">
                                <div class="flex group-hover/run:hidden size-10 items-center justify-center"
                                     v-if="element.last_run_status === 'SUCCESS' || element.last_run_status === 'NOT_STARTED'"
                                >
                                    <svg viewBox="0 0 24 24" role="presentation" aria-label="Run" class="size-6 " style="color: rgb(0, 168, 50);"><path d="M20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4C12.76,4 13.5,4.11 14.2,4.31L15.77,2.74C14.61,2.26 13.34,2 12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12M7.91,10.08L6.5,11.5L11,16L21,6L19.59,4.58L11,13.17L7.91,10.08Z" style="fill: currentcolor;"></path></svg>
                                </div>
                                <div class="flex group-hover/run:hidden size-10 items-center justify-center"
                                     v-if="element.last_run_status === 'ERROR'"
                                >
                                    <svg class="size-6 fill-red-500" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M14.59 8L12 10.59 9.41 8 8 9.41 10.59 12 8 14.59 9.41 16 12 13.41 14.59 16 16 14.59 13.41 12 16 9.41 14.59 8zM12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"></path></svg>
                                </div>
                                <div
                                    class="flex size-10 items-center justify-center"
                                    v-if="element.last_run_status === 'RUNNING'"
                                >
                                    <LoadingSvg class="size-6 animate-spin"/>
                                </div>
                                <ToolTip text="Запустите соединение" position="bottom">
                                    <div class="hidden group-hover/run:flex rounded-full size-10 hover:bg-gray-200 items-center justify-center cursor-pointer"
                                         v-on:click="startConnection(element.id)"
                                         v-if="element.last_run_status !== 'RUNNING'"
                                    >
                                        <PlayCircle class="size-6"/>
                                    </div>
                                </ToolTip>
                            </div>
                            <ConnectionSettingsPopup
                                :workflow-id="props.workflow_id"
                                :connection-id="element.id"
                                @start-connection="startConnection(element.id)"
                                @delete-connection="deleteConnection"
                                @open-summary="openSummary"
                                v-model="element.isPopupVisible"
                            />
                        </div>
                    </div>
                </template>
            </draggable>
        </div>

    </div>
</template>

<script setup>
import {  ref, onBeforeMount, defineProps, defineModel, watch, defineEmits } from 'vue';
import { Api } from '@/service/api';
import draggable from 'vuedraggable'
import PlayCircle from '@/components/UI/PlayCircle.vue';
import FileSvg from '@/components/UI/FileSvg.vue';
import EditFileSvg from '@/components/UI/EditFileSvg.vue';
import LoadingSvg from "@/components/UI/LoadingSvg.vue";
import {datetimeOptions} from "@/service/options";
import ConnectionSettingsPopup from "@/pages/Flow/ConnectionSettingsPopup.vue";
import PlusCircleSvg from "@/components/UI/svg/PlusCircleSvg.vue";
import ToolTip from "@/components/UI/ToolTip.vue";

const currentState = ref('Соединения')
const connections = ref([])

const reloadConnections = defineModel("reload-connections")

const emits = defineEmits(['start-connection', 'stop-connection', 'hover-on', 'hover-off', 'delete-connection', 'open-summary'])

const refreshTrigger = defineModel('refreshTrigger')

watch(reloadConnections, async (newVal) => {
    if (newVal) {
        // Вызываем нужную функцию
        await updateStatusesForAll()

        // Сбрасываем значение обратно
        reloadConnections.value = false
    }
})

const isPopupVisible = ref(false)

const props = defineProps({
    workflow_id: String
})

const files = ref([])

onBeforeMount(async () => {
    connections.value = await Api.getWorkflowConnections(props.workflow_id)
    for (let i = 0; i < connections.value.length; i++) {
        connections.value[i].isPopupVisible = false
    }
    for (const conn of connections.value) {
        if (conn.last_run_status === "RUNNING") {
            setTimeout(async function () {
                await updateStatus(conn.id);
            }, 1000)
        }
    }
    files.value = await Api.getWorkflowFiles(props.workflow_id)
})

function setState(value) {
    currentState.value = value
}

function openSummary(connection_id) {
    emits('open-summary', connection_id)
}


async function updateStatusesForAll() {
    for (const conn of connections.value) {
        conn.last_run_status = "RUNNING"
        setTimeout(async function () {
            await updateStatus(conn.id);
        }, 1000)
    }
}

async function deleteConnection(value) {
    emits("delete-connection", value)
}

async function updateStatus(connection_id) {
    try {
        const conn = await Api.getConnectionStatus(props.workflow_id, connection_id);

        const connectionIndex = connections.value.findIndex(c => c.id === conn.id);
        if (connectionIndex !== -1) {
            connections.value[connectionIndex].last_run_status = conn.last_run_status;
            connections.value[connectionIndex].last_run_datetime = conn.last_run_datetime;
        }
        if (conn.last_run_status === "RUNNING") {
            emits('start-connection', connection_id)
            setTimeout(() => updateStatus(connection_id), 1000);
        } else {
            emits('stop-connection', connection_id);
            refreshTrigger.value = true
        }
    } catch (error) {
        console.error('Error updating connection status:', error);
    }
}

function getFormattedDatetime(datetime) {
    const date = new Date(datetime)
    return `${date.toLocaleDateString('ru-RU', datetimeOptions)}`
}

/*async function updateStatuses() {
    const cons = await Api.getConnectionsStatuses(props.workflow_id)

    cons.forEach(conn => {
        for (let i = 0; i < connections.value.length; i++) {
            if (conn.id === connections.value[i].id) {
                connections.value[i].last_run_status = conn.last_run_status
            }
        }
    })

}*/

async function startConnection(connection_id) {
    for (let i = 0; i < connections.value.length; i++) {
        if (connections.value[i].id === connection_id) {
            connections.value[i].last_run_status = "RUNNING"
            connections.value[i].isPopupVisible = false
        }
    }
    await Api.startConnection(props.workflow_id, connection_id)
    isPopupVisible.value = false
    await updateStatus(connection_id);
    emits('start-connection', connection_id)
}

async function updatePosition(){
    for (let i = 0; i < connections.value.length; i++) {
        connections.value[i].order_id = i + 1
    }
    let connections_request = []

    connections.value.forEach((connection) => {
        connections_request.push({
            "connection_id": connection.id,
            "order_id": connection.order_id
        })
    })

    await Api.updateConnectionsOrder(props.workflow_id, connections_request)
}

function openSpreadsheet(spreadsheet_id, tab_id) {
    let url = "https://docs.google.com/spreadsheets/d/" + spreadsheet_id + "/edit?gid=" + tab_id
    window.open(url, '_blank')
}

</script>

<style scoped>
    .animate-expand {
        animation-duration: 300ms;
        animation-name: expand;
    }

    @keyframes expand {
        0% {
            transform: scaleY(0);
        }

        100% {
            transform: scaleY(1);
        }
    }
</style>